// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT ACTION MODAL — champs & fragments de rendu purs
// Extrait de index.ts (découpe god-module 2026-07) : builders de fragments HTML
// PURS (aucun état de closure, aucun effet de bord) sortis de la fonction-monstre
// `renderActionEditor` pour la dégraisser. Chaque fonction ne dépend que de ses
// arguments explicites + des globals ambiants — comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t, userLabel,
// sevColor.

import type { ActionUser, ActionStatusOption } from './index.js';

/** Élément « risque origine » d'une action (forme figée par le backend). */
export interface ActionOrigin {
  project_code?: string | null;
  project_name?: string | null;
  risk_id?: number | null;
  risk_code?: string | null;
  risk_category?: string | null;
  risk_consequence?: string | null;
  severity?: number | null;
}

/**
 * Rendu des risques-origine d'une action : code projet (tooltip = libellé) /
 * code risque (fallback « R<id> ») / événement redouté tronqué à 30 char
 * (tooltip = version complète) / pastille sévérité colorée. Vide → libellé
 * « aucune origine ».
 */
export function renderOriginsHtml(origins: ActionOrigin[] | undefined): string {
  // Snap-vers-la-sévérité-disponible via le `sevColor()` global ; fond neutre
  // si sévérité inconnue.
  const sevColorOr = (sev: number | null | undefined): string => {
    if (sev == null) return 'var(--bg3)';
    return sevColor(sev);
  };
  // Texte noir/blanc selon la luminance du fond (WCAG simplifié) pour rester
  // lisible quel que soit le thème de couleur paramétré par l'admin.
  const textOnHex = (hex: string): string => {
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m) return '#1a1a1a';
    const code = m[1] as string;
    const r = parseInt(code.substring(0, 2), 16);
    const g = parseInt(code.substring(2, 4), 16);
    const b = parseInt(code.substring(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? '#1a1a1a' : '#ffffff';
  };
  const truncOrigin = (s: string, n = 30): string =>
    s.length > n ? s.substring(0, n - 1) + '…' : s;
  return origins && origins.length > 0
    ? origins
        .map((o) => {
          const pCode = o.project_code || '?';
          const pName = o.project_name || '';
          const rCode = o.risk_code || (o.risk_id != null ? `R${o.risk_id}` : '?');
          const rEvent = o.risk_consequence || o.risk_category || '—';
          const rEventShort = truncOrigin(rEvent);
          const sev = o.severity ?? null;
          const sevTitle = sev != null ? `Sévérité ${sev}` : 'Sévérité non disponible';
          const sevBg = sevColorOr(sev);
          const sevTxt = sev != null ? textOnHex(sevBg) : 'var(--text2)';
          const sevLabel = sev != null ? String(sev) : '?';
          return `<span style="display:inline-flex;align-items:center;gap:6px;font-size:11px;background:var(--bg3);padding:3px 8px;border-radius:8px;margin:0 4px 4px 0;line-height:1.4">
              <span title="${esc(pName)}" style="font-weight:600;color:var(--text2);cursor:help">${esc(pCode)}</span>
              <span style="color:var(--text3)">/</span>
              <span style="font-weight:600">${esc(rCode)}</span>
              <span style="color:var(--text3)">/</span>
              <span title="${esc(rEvent)}" style="cursor:help">${esc(rEventShort)}</span>
              <span title="${esc(sevTitle)}" style="display:inline-block;min-width:22px;padding:1px 6px;text-align:center;background:${sevBg};color:${sevTxt};border-radius:6px;font-weight:700;font-size:11px;line-height:1.4">${esc(sevLabel)}</span>
            </span>`;
        })
        .join('')
    : `<span style="color:var(--text3);font-size:12px">${esc(t('actionModal.field.originsEmpty'))}</span>`;
}

/** `<option>` d'un `<select>` de personnes (approbateur) : placeholder « — »
 * puis chaque individu. `selectedId` marque l'option courante. */
export function buildApproverOptions(
  individuals: ActionUser[],
  selectedId: number | null | undefined,
): string {
  return (
    '<option value="">—</option>' +
    individuals
      .map(
        (u) =>
          `<option value="${u.id}" ${u.id === selectedId ? 'selected' : ''}>${esc(userLabel(u, false))}</option>`,
      )
      .join('')
  );
}

/** `<option>` d'un `<select>` d'owner : TOUS les users (personne OU équipe —
 * cf. décision juin 2026), le dropdown restreint ensuite aux A∪R courants. */
export function buildOwnerOptions(
  users: ActionUser[],
  selectedId: number | null | undefined,
): string {
  return (
    '<option value="">—</option>' +
    users
      .map(
        (u) =>
          `<option value="${u.id}" ${u.id === selectedId ? 'selected' : ''}>${esc(userLabel(u, false))}</option>`,
      )
      .join('')
  );
}

/** `<option>` d'un `<select>` de statut : placeholder i18n puis les statuts
 * (déjà filtrés/triés par l'appelant). `selectedId` marque le statut effectif. */
export function buildStatusOptions(
  sortedStatuses: ActionStatusOption[],
  selectedId: number | null | undefined,
): string {
  return (
    `<option value="">${esc(t('actionModal.statusPlaceholder'))}</option>` +
    sortedStatuses
      .map(
        (s) =>
          `<option value="${s.id}" ${s.id === selectedId ? 'selected' : ''}>${esc(s.name)}</option>`,
      )
      .join('')
  );
}

/** Puces RACI (utilisateurs sélectionnés d'un champ), avec bouton de retrait
 * par puce. Vide → libellé i18n « aucun ». */
function renderRaciChips(
  fieldId: string,
  selectedIds: number[] | undefined,
  users: ActionUser[],
): string {
  const sel = (selectedIds || [])
    .map((id) => users.find((u) => u.id === id))
    .filter((u): u is ActionUser => Boolean(u));
  return sel.length > 0
    ? sel
        .map(
          (u) => `<span class="raci-chip" data-uid="${u.id}" data-field="${fieldId}"
      style="display:inline-flex;align-items:center;gap:3px;background:var(--accent-bg);color:var(--accent);
             font-size:11px;padding:2px 7px;border-radius:10px;margin:2px;cursor:default">
      ${esc(userLabel(u, true))}
      <button data-remove-raci="${u.id}" data-field="${fieldId}"
        style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button>
    </span>`,
        )
        .join('')
    : `<span class="raci-empty" style="color:var(--text3);font-size:11px">${esc(t('actionModal.raci.empty'))}</span>`;
}

/** Bloc RACI complet d'un champ : conteneur + puces + bouton « + ». */
export function renderRaciBlock(
  fieldId: string,
  selectedIds: number[] | undefined,
  users: ActionUser[],
): string {
  return `<div class="raci-block" id="raci-${fieldId}"
        style="border:1px solid var(--border2);border-radius:var(--radius);padding:6px 8px;margin-top:4px;display:flex;align-items:flex-start;gap:6px">
      <div class="raci-chips" id="chips-${fieldId}" style="flex:1;min-height:22px;display:flex;flex-wrap:wrap;gap:2px">
        ${renderRaciChips(fieldId, selectedIds, users)}
      </div>
      <button class="raci-add-btn btn btn-secondary btn-icon" data-raci-field="${fieldId}"
        style="flex-shrink:0;font-size:14px;line-height:1;padding:3px 7px;align-self:flex-start" title="${esc(t('actionModal.raci.btnAddTitle'))}">+</button>
    </div>`;
}

/**
 * Rangée de boutons du bas de l'éditeur (Fermer / Annuler / Enregistrer ▾).
 *
 * Affichage conditionnel, piloté par `_updateCtaVisibility` côté index.ts :
 * - éditeur ouvert sans modification → seul « Fermer », pour sortir franchement
 *   sans passer par « Annuler » ;
 * - dès qu'il y a quelque chose à enregistrer → « Annuler » + le split-button.
 *   C'est le cas D'EMBLÉE sur une création pré-remplie (cf. `startDirty`).
 */
export function renderEditorCtas(): string {
  return `<div style="display:flex;gap:6px;align-items:center;justify-content:flex-end;position:relative">
          <button id="ea-btn-close" class="btn btn-primary" type="button"
            style="width:170px;height:36px">${esc(t('common.close'))}</button>
          <button id="ea-btn-cancel" class="btn btn-neutral" type="button"
            style="width:170px;height:36px;display:none">${esc(t('common.cancel'))}</button>
          <!-- Split-button : "Enregistrer" (170px) + chevron ▾ (32px), même
               largeur pour le menu déroulant. -->
          <div id="ea-btn-save-group" style="display:none;align-items:stretch">
            <button id="ea-btn-save" class="btn btn-primary" type="button"
              style="width:170px;height:36px;border-top-right-radius:0;border-bottom-right-radius:0;margin-right:1px">
              ${esc(t('common.save'))}
            </button>
            <button id="ea-btn-save-menu" class="btn btn-primary" type="button"
              title="${esc(t('actionModal.btn.saveSilent'))}" aria-label="${esc(t('actionModal.btn.saveSilent'))}" aria-haspopup="menu" aria-expanded="false"
              style="width:32px;height:36px;border-top-left-radius:0;border-bottom-left-radius:0;padding:0;font-size:14px">▾</button>
          </div>
          <div id="ea-save-menu" role="menu" style="display:none;position:absolute;bottom:calc(100% + 4px);right:32px;background:var(--bg2);border:1px solid var(--border2);border-radius:var(--radius);box-shadow:0 8px 24px rgba(0,0,0,.15);width:170px;z-index:1000;overflow:hidden">
            <button id="ea-btn-save-no-notify" type="button" role="menuitem"
              style="display:block;width:100%;text-align:center;padding:8px 10px;background:transparent;border:none;cursor:pointer;font-size:12px;color:var(--text);font-weight:500">
              ${esc(t('actionModal.btn.saveSilentShort'))}
            </button>
          </div>
        </div>`;
}

/** Entrées de la zone sticky-top de l'éditeur (rendu pur). */
/** Les champs de l'action lus par la zone sticky-top. */
export interface EditorHeaderRowsAction {
  text?: string;
  is_confidential?: boolean;
  expected_deliverable?: string | null;
  external_link?: string | null;
}

export interface EditorHeaderRowsInput {
  /** Code affiché (readonly) : id de l'action, ou « — » en création. */
  codeDefault: string;
  /** Priorité déjà normalisée sur le barème 0..4 (cf. domain/action-form). */
  priority: number;
  /** L'utilisateur courant peut-il basculer la confidentialité ? */
  canToggleConf: boolean;
}

/**
 * La rangée sticky-top : Code | Titre | Priorité | Confidentiel.
 *
 * Livrable attendu et Lien (URL) en sont SORTIS le 2026-08-26 : ils y avaient
 * été mis en août pour rester au-dessus de la séparation, mais deux rangées
 * collées dans une zone `position:sticky` mangeaient la hauteur utile à chaque
 * scroll. Ils vivent maintenant dans leur propre bloc, juste en dessous — cf.
 * `renderEditorDeliverableBlock`.
 */
export function renderEditorHeaderRows(
  a: EditorHeaderRowsAction,
  input: EditorHeaderRowsInput,
): string {
  const { codeDefault, priority, canToggleConf } = input;
  const text = a.text || '';
  const isConfidential = !!a.is_confidential;
  const priorityOptions = ['0', '1', '2', '3', '4']
    .map(
      (v) =>
        `<option value="${v}" ${parseInt(v, 10) === priority ? 'selected' : ''}>${esc(t('actionModal.priority.' + v))}</option>`,
    )
    .join('');
  return `<div class="ea-top-row" style="display:flex;gap:10px;align-items:flex-end">
        <div class="ea-field-code" style="width:90px;flex-shrink:0">
          <label style="font-size:12px;color:var(--text3)">${esc(t('actionModal.field.code'))}</label>
          <input type="text" id="ea-code" value="${esc(codeDefault)}" readonly tabindex="-1"
            style="width:100%;font-size:12px;margin-top:3px;background:var(--bg3);color:var(--text2);cursor:default">
        </div>
        <div class="ea-field-title" style="flex:1">
          <label style="font-size:12px;color:var(--text3)">${esc(t('actionModal.field.desc'))}</label>
          <input type="text" id="ea-text" value="${esc(text)}"
            placeholder="${esc(t('actionModal.field.descPlaceholder'))}"
            style="width:100%;font-size:12px;margin-top:3px">
        </div>
        <div class="ea-field-priority" style="width:160px;flex-shrink:0">
          <label style="font-size:12px;color:var(--text3)">${esc(t('actionModal.field.priority'))}</label>
          <select id="ea-priority" style="width:100%;margin-top:3px">${priorityOptions}</select>
        </div>
        <div class="ea-field-confidential" style="width:150px;flex-shrink:0">
          <label style="font-size:12px;color:var(--text3)">${esc(t('actionModal.field.confidential'))}</label>
          <label style="display:flex;align-items:center;gap:6px;margin-top:6px;font-size:12px;${canToggleConf ? 'cursor:pointer' : 'opacity:.55'}"${canToggleConf ? '' : ` title="${esc(t('actionModal.confidential.lockedHint'))}"`}>
            <input type="checkbox" id="ea-confidential" ${isConfidential ? 'checked' : ''} ${canToggleConf ? '' : 'disabled'}>
            <span>${esc(t('actionModal.confidential.hint'))}</span>
          </label>
        </div>
      </div>`;
}

/**
 * Toute la zone haute de l'éditeur : la bande sticky, puis le bloc livrable.
 *
 * Extraite d'`index.ts` le 2026-08-26 en même temps que la sortie du livrable
 * hors du sticky — le cliquet de taille refuse qu'un god-module grossisse, et
 * cette zone n'avait rien à faire dans la fonction de 1100 lignes : elle
 * n'assemble que des rendus déjà définis ici.
 *
 * `position:sticky` garde la bande visible quel que soit le défilement du host.
 * `prefixHtml` (le fil d'Ariane de `pages.action`) défile solidaire de la ligne
 * code/titre/priorité — l'utilisateur ne perd jamais le contexte.
 */
export function renderEditorTopZone(
  a: EditorHeaderRowsAction,
  input: EditorHeaderRowsInput,
  prefixHtml: string,
): string {
  return `<div class="ea-sticky-top" style="position:sticky;top:0;z-index:5;flex-shrink:0;display:flex;flex-direction:column;gap:8px;padding:10px 0;background:var(--bg);border-bottom:1px solid var(--border)">
      ${prefixHtml}
      ${renderEditorHeaderRows(a, input)}
    </div>
    ${renderEditorDeliverableBlock(a)}`;
}

/**
 * Livrable attendu et Lien (URL), avec ses deux CTA, dans un bloc à eux —
 * juste SOUS la zone sticky (demande Christophe, 2026-08-26).
 *
 * Les deux champs vont ensemble : le livrable dit ce qu'on attend, le lien dit
 * où il se trouve. Les séparer obligeait à chercher l'un après avoir rempli
 * l'autre. Hors du sticky, ils ne coûtent plus de hauteur permanente au
 * défilement — c'est ce que leur place précédente faisait payer à chaque scroll.
 */
export function renderEditorDeliverableBlock(a: EditorHeaderRowsAction): string {
  const expectedDeliverable = a.expected_deliverable || '';
  const externalLink = a.external_link || '';
  return `<div class="ea-extra-row" style="display:flex;gap:10px;align-items:flex-end;padding:10px 0;border-bottom:1px solid var(--border)">
        <div class="ea-field-deliverable" style="flex:1">
          <label style="font-size:12px;color:var(--text3)">${esc(t('actionModal.field.deliverable'))}</label>
          <input type="text" id="ea-deliverable" value="${esc(expectedDeliverable)}"
            placeholder="${esc(t('actionModal.field.deliverablePlaceholder'))}"
            style="width:100%;font-size:12px;margin-top:3px">
        </div>
        <div class="ea-field-external-link" style="flex:1">
          <label style="font-size:12px;color:var(--text3)" for="ea-external-link">${esc(t('actionModal.field.externalLink'))}</label>
          <div style="display:flex;gap:4px;align-items:center;margin-top:3px">
            <input type="url" id="ea-external-link" value="${esc(externalLink)}" inputmode="url"
              maxlength="2048" placeholder="${esc(t('actionModal.field.externalLinkPlaceholder'))}"
              style="flex:1;min-width:0;font-size:12px">
            <button type="button" id="ea-external-link-open" class="btn btn-secondary btn-icon"
              title="${esc(t('actionModal.externalLink.open'))}" aria-label="${esc(t('actionModal.externalLink.open'))}"
              style="flex-shrink:0">↗</button>
            <button type="button" id="ea-external-link-copy" class="btn btn-secondary btn-icon"
              title="${esc(t('actionModal.externalLink.copy'))}" aria-label="${esc(t('actionModal.externalLink.copy'))}"
              style="flex-shrink:0">⧉</button>
          </div>
        </div>
      </div>`;
}
